// WebSocket Hook for Qbus
// Provides real-time Qbus output state updates via Socket.IO connection to /qbus namespace

import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';

/**
 * Socket.IO hook for Qbus real-time updates
 * Connects to the /qbus namespace on the API server
 * @param {Object} options - Configuration options
 * @param {number} options.locationId - Location ID to subscribe to
 * @param {boolean} options.autoConnect - Auto-connect on mount (default: true)
 * @param {number} options.reconnectAttempts - Max reconnect attempts (default: 5)
 * @param {Function} options.onOutputStateChanged - Handler for output state changes
 * @param {Function} options.onControllerStatusChanged - Handler for controller status changes
 * @param {Function} options.onConfigUpdated - Handler for config updates
 * @returns {Object} Socket state and methods
 */
export const useQbusWebSocket = (options = {}) => {
  const {
    locationId,
    autoConnect = true,
    reconnectAttempts = 5,
    onOutputStateChanged,
    onControllerStatusChanged,
    onConfigUpdated
  } = options;

  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);

  const socketRef = useRef(null);
  const subscribedRoomRef = useRef(null);

  // Refs for callbacks so socket handlers always call the latest version
  const onOutputStateChangedRef = useRef(onOutputStateChanged);
  onOutputStateChangedRef.current = onOutputStateChanged;
  const onControllerStatusChangedRef = useRef(onControllerStatusChanged);
  onControllerStatusChangedRef.current = onControllerStatusChanged;
  const onConfigUpdatedRef = useRef(onConfigUpdated);
  onConfigUpdatedRef.current = onConfigUpdated;

  const getSocketUrl = useCallback(() => {
    return process.env.REACT_APP_API_URL || 'https://api.caro.care';
  }, []);

  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      return;
    }

    try {
      const socketUrl = getSocketUrl();
      console.log('[QbusWebSocket] Connecting to:', socketUrl, '/qbus');

      const socket = io(`${socketUrl}/qbus`, {
        transports: ['websocket', 'polling'],
        reconnectionAttempts: reconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000,
        autoConnect: true
      });

      socket.on('connect', () => {
        console.log('[QbusWebSocket] Connected, socket ID:', socket.id);
        setConnectionStatus('connected');
        setError(null);

        // Auto-subscribe to location room on connect/reconnect
        if (locationId) {
          const roomData = { locationId };
          socket.emit('subscribe', roomData);
          subscribedRoomRef.current = `location:${locationId}`;
        }
      });

      socket.on('connected', (data) => {
        console.log('[QbusWebSocket] Server confirmed connection:', data);
      });

      socket.on('subscribed', (data) => {
        console.log('[QbusWebSocket] Subscribed to channel:', data.channel);
      });

      socket.on('disconnect', (reason) => {
        console.log('[QbusWebSocket] Disconnected:', reason);
        setConnectionStatus('disconnected');
      });

      socket.on('connect_error', (err) => {
        console.log('[QbusWebSocket] Connection error:', err.message);
        setConnectionStatus('error');
        setError(err.message);
      });

      socket.on('reconnect_attempt', (attemptNumber) => {
        console.log('[QbusWebSocket] Reconnect attempt:', attemptNumber);
        setConnectionStatus('reconnecting');
      });

      socket.on('reconnect', () => {
        console.log('[QbusWebSocket] Reconnected');
        setConnectionStatus('connected');
        setError(null);
      });

      socket.on('reconnect_failed', () => {
        console.log('[QbusWebSocket] Reconnect failed');
        setConnectionStatus('unavailable');
        setError('Unable to connect to Qbus WebSocket');
      });

      // Qbus events
      socket.on('output_state_changed', (data) => {
        console.log('[QbusWebSocket] output_state_changed:', data);
        setLastMessage(data);
        if (onOutputStateChangedRef.current) {
          onOutputStateChangedRef.current(data);
        }
      });

      socket.on('controller_status_changed', (data) => {
        console.log('[QbusWebSocket] controller_status_changed:', data);
        setLastMessage(data);
        if (onControllerStatusChangedRef.current) {
          onControllerStatusChangedRef.current(data);
        }
      });

      socket.on('config_updated', (data) => {
        console.log('[QbusWebSocket] config_updated:', data);
        setLastMessage(data);
        if (onConfigUpdatedRef.current) {
          onConfigUpdatedRef.current(data);
        }
      });

      socketRef.current = socket;
    } catch (err) {
      console.error('[QbusWebSocket] Connection failed:', err.message);
      setError(err.message);
      setConnectionStatus('error');
    }
  }, [getSocketUrl, reconnectAttempts, locationId]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      console.log('[QbusWebSocket] Disconnecting...');
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    subscribedRoomRef.current = null;
    setConnectionStatus('disconnected');
  }, []);

  const subscribe = useCallback((data = {}) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('subscribe', data);
    }
  }, []);

  const unsubscribe = useCallback((data = {}) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('unsubscribe', data);
    }
  }, []);

  // Auto-connect on mount, cleanup on unmount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [autoConnect, connect]);

  // Re-subscribe when locationId changes
  useEffect(() => {
    if (!socketRef.current?.connected || !locationId) return;

    // Unsubscribe from old room
    if (subscribedRoomRef.current) {
      const oldLocId = subscribedRoomRef.current.replace('location:', '');
      if (oldLocId !== String(locationId)) {
        socketRef.current.emit('unsubscribe', { locationId: parseInt(oldLocId) });
      }
    }

    // Subscribe to new room
    socketRef.current.emit('subscribe', { locationId });
    subscribedRoomRef.current = `location:${locationId}`;
  }, [locationId]);

  return {
    connectionStatus,
    lastMessage,
    error,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    isConnected: connectionStatus === 'connected',
    isUnavailable: connectionStatus === 'unavailable' || connectionStatus === 'error',
    socket: socketRef.current
  };
};

export default useQbusWebSocket;
